import { useFeatureFlags } from '@previo/feature-flags'
import type { LocationQuery, NavigationGuard, RouteLocation } from 'vue-router'
import { defineComponent, h } from 'vue'
import { Routes } from '~/router/routes'
import { usePreferredPMSVersion } from '~/composables/usePreferredPMSVersion'
import { usePmsVersionOverride } from '~/composables/usePmsVersionOverride'

// Prevent unchecked input for loop condition (SAST)
const MAX_PARAMS_ENTRIES = 50
const MAX_QUERY_ENTRIES = 50

// Vue Router's requirement that non-redirect routes declare a component.
const UnreachableRouteComponentPlaceholder = defineComponent({
  setup() {
    return () => h('div')
  },
})

export const redirectToP1PMS = (proxyRoute: RouteLocation, p1Path: string): void => {
  const selectedPropertyId: number = Number(proxyRoute.params.propertyId)
  let url: string = import.meta.env.PMS_FE_P1_APP_URL + `/${selectedPropertyId}/` + p1Path

  // nosemgrep: nodejs_scan.javascript-dos-rule-layer7_object_dos
  const paramEntries = Object.entries(proxyRoute.params).slice(0, MAX_PARAMS_ENTRIES)
  paramEntries.forEach(([key, value]) => {
    url = url.replace(`:${key}`, String(value))
  })

  const query: LocationQuery = proxyRoute.query
  if (proxyRoute.query) {
    // nosemgrep: nodejs_scan.javascript-dos-rule-layer7_object_dos
    const queryEntries = Object.entries(query).slice(0, MAX_QUERY_ENTRIES)
    const params: string[][] = queryEntries.map(([key, value]) => [key, String(value)])

    if (params.length) {
      const [baseUrl, hash] = url.split('#')
      const queryString = new URLSearchParams(params).toString()
      url = `${baseUrl}?${queryString}${hash ? `#${hash}` : ''}`
    }
  }

  window.location.replace(url)
}

const isRouterPreferenceDisabled = (): boolean =>
  import.meta.env.PMS_FE_ROUTER_PREFERENCE_ENABLED === 'false'

// Always redirects to P1 PMS, regardless of user preference. Used for routes that don't have a P2 equivalent.
const guardRedirectToP1 =
  (p1Path: string): NavigationGuard =>
  (to, _from) => {
    redirectToP1PMS(to, p1Path)

    return false
  }

// Falls back to a P2 route when the router-preference feature is disabled.
const guardRedirectToP1WithP2Fallback =
  (p1Path: string, p2Route: Routes): NavigationGuard =>
  (to, _from, next) => {
    if (isRouterPreferenceDisabled()) {
      next({ name: p2Route })
    } else {
      redirectToP1PMS(to, p1Path)

      return false
    }
  }

// Goes to P1 only when the user prefers the current (P1) version. Otherwise, routes to the P2 equivalent.
// A forced user (the AUTOMATIC_REDIRECT_TO_P2 flag or their override row) ignores the cookie: P2 always wins.
const guardRedirectByCookiePreference =
  (p1Path: string, p2Route: Routes): NavigationGuard =>
  async (to, _from, next) => {
    if (isRouterPreferenceDisabled()) {
      next({ name: p2Route, query: to.query })
      return
    }

    const ff = useFeatureFlags()
    await ff.waitForInit()

    if (usePmsVersionOverride().isForced.value) {
      next({ name: p2Route, query: to.query })
      return
    }

    if (!usePreferredPMSVersion().usesCurrent()) {
      next({ name: p2Route, query: to.query })
      return
    }

    redirectToP1PMS(to, p1Path)

    return false
  }

export const ProxyP1routes = [
  {
    path: 'reservation/canvas',
    name: Routes.CALENDAR_INDEX_P1_P2,
    beforeEnter: guardRedirectByCookiePreference('reservation/canvas', Routes.CALENDAR_INDEX),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'reservation/grid',
    name: Routes.RESERVATIONS_INDEX_P1_P2,
    beforeEnter: guardRedirectByCookiePreference('reservation/grid', Routes.RESERVATIONS_INDEX),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'reservation-room/show/:reservationRoomId(\\d+)',
    name: Routes.RESERVATION_ROOM_SHOW,
    beforeEnter: ((to) => {
      redirectToP1PMS(
        { ...to, query: { comId: to.params.reservationRoomId } } as RouteLocation,
        'commission/show',
      )

      return false
    }) satisfies NavigationGuard,
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'guests/grid',
    name: Routes.GUESTS_INDEX_P1_P2,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1WithP2Fallback('guests/grid', Routes.GUESTS_INDEX),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'partners/list',
    name: Routes.COMPANIES_INDEX_P1_P2,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1WithP2Fallback('partners/list', Routes.COMPANIES_INDEX),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'guest_book/grid',
    name: Routes.GUEST_BOOK_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('guest_book/grid'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'guest_evidence/grid',
    name: Routes.GUEST_EVIDENCE_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('guest_evidence/grid'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'mailing',
    name: Routes.MAILING_INDEX_P1_P2,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1WithP2Fallback(
      'guests/mailing#mailing',
      Routes.MAILING_INDEX_AUTOMATED,
    ),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'mailing-p1',
    name: Routes.MAILING_INDEX_P1,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('guests/mailing#mailing'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'communication/grid#Communication',
    name: Routes.COMMUNICATION_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('communication/grid#Communication'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'benefit_program/cards',
    name: Routes.BENEFIT_PROGRAM_CARDS_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('benefit_program#benefitProgram/cards'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'benefit_program/turnover',
    name: Routes.BENEFIT_PROGRAM_TURNOVER_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('benefit_program/turnover#benefitProgram/turnover'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'invoicing/documents',
    name: Routes.INVOICING_DOCUMENTS_INDEX_P1_P2,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectByCookiePreference('invoices', Routes.INVOICING_DOCUMENTS_INDEX),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'invoicing/folios',
    name: Routes.FOLIO_INDEX_P1_P2,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('invoicing/folios#folios'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'invoicing/payments/cashbook',
    name: Routes.INVOICING_PAYMENTS_CASH_BOOK_INDEX_P1_P2,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1WithP2Fallback(
      'cash_desk/cash_book',
      Routes.INVOICING_PAYMENTS_CASH_BOOK_INDEX,
    ),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'invoicing/payments/bank-transfer',
    name: Routes.INVOICING_PAYMENTS_BANK_TRANSFER_INDEX_P1_P2,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1WithP2Fallback(
      'cash_desk/banking_book',
      Routes.INVOICING_PAYMENTS_BANK_TRANSFER_INDEX,
    ),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'invoicing/payments/terminal-payment',
    name: Routes.INVOICING_PAYMENTS_TERMINAL_PAYMENT_INDEX_P1_P2,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1WithP2Fallback(
      'cash_desk/terminal_payments',
      Routes.INVOICING_PAYMENTS_TERMINAL_PAYMENT_INDEX,
    ),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'invoicing/payments/online-payment',
    name: Routes.INVOICING_PAYMENTS_ONLINE_PAYMENT_INDEX_P1_P2,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1WithP2Fallback(
      'cash_desk/online_payments',
      Routes.INVOICING_PAYMENTS_ONLINE_PAYMENT_INDEX,
    ),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'invoicing/payments/coupon',
    name: Routes.INVOICING_PAYMENTS_COUPON_INDEX_P1_P2,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1WithP2Fallback(
      'cash_desk/coupon',
      Routes.INVOICING_PAYMENTS_COUPON_INDEX,
    ),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'stock_reserves',
    name: Routes.STOCK_TRANSACTIONS_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('stock_reserves'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'cash_desk/balance',
    name: Routes.CLOSURE_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('cash_desk/balance'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'exchanges',
    name: Routes.EXCHANGES_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('exchanges'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'rates/currency_settings',
    name: Routes.EXCHANGE_RATES_INDEX_P1,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('rates/currency_settings'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'invoice_settings/accounting',
    name: Routes.ACCOUNTING_ASSIGNMENT_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('invoice_settings/accounting'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'invoice_settings/vat_classification',
    name: Routes.VAT_CLASSIFICATION_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('invoice_settings/vat_classification'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'invoice_settings/service_classification',
    name: Routes.PKWIU_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('invoice_settings/service_classification'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'invoice_settings/gtu_codes',
    name: Routes.GTU_CODES_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('invoice_settings/gtu_codes'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'stats/log',
    name: Routes.USER_ACTIVITY_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('stats/log'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'guest_stats_report/grid',
    name: Routes.STATISTICS_REPORTS_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('guest_stats_report/grid'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'daily_overview',
    name: Routes.DAILY_OVERVIEW_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('daily_overview#dailyOverview'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'tax_report/grid',
    name: Routes.VISITOR_TAX_REPORT_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('tax_report/grid'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'guest_review',
    name: Routes.REVIEWS_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('guest_review#guestReviews'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'stats/bonus_report',
    name: Routes.COMMISSIONS_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('stats/bonus_report'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'ntak_report',
    name: Routes.NTAK_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('ntak_report'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'ntak_report/charges',
    name: Routes.NTAK_REVENUES_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('ntak_report/charges'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'ntak_report/spending',
    name: Routes.NTAK_EXPENSES_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('ntak_report/spending'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'ntak_report/connection',
    name: Routes.NTAK_CONNECTION_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('ntak_report/connection'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'viza_report/status',
    name: Routes.VISA_STATISTICS_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('viza_report/status'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'alfred_dashboard',
    name: Routes.ALFRED_DASHBOARD_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('alfred_dashboard'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'reports/occupancy',
    name: Routes.REPORTS_OCCUPANCY_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('reports/occupancy'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'reports/reservations',
    name: Routes.REPORTS_RESERVATION_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('reports/reservations'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'reports/pickup',
    name: Routes.REPORTS_PICKUP_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('reports/pickup'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'reports/accounts',
    name: Routes.REPORTS_ACCOUNTS_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('reports/accounts'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'reports/overview',
    name: Routes.REPORTS_OVERVIEW_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('reports/overview'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'reports/hotel_group_overview',
    meta: {
      isMobileFirst: false,
    },
    name: Routes.REPORTS_HOTELGROUP_OVERVIEW_INDEX,
    beforeEnter: guardRedirectToP1('reports/hotel_group_overview'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'reports/ntak_kpi_report',
    meta: {
      isMobileFirst: false,
    },
    name: Routes.REPORTS_NTAK_KPI_INDEX,
    beforeEnter: guardRedirectToP1('ntak_kpi_report'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'rates',
    name: Routes.PRICE_LIST_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('rates'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'rates/meals',
    name: Routes.MEALS_LIST_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('rates/meals'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'price_actions/revenue',
    name: Routes.REVENUE_MANAGEMENT_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('price_actions/revenue#priceActions/revenueManagement'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'tax_settings',
    name: Routes.VISITOR_TAX_INDEX_P1,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('tax_settings#taxSettings'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'price_actions/longTerm',
    name: Routes.DISCOUNT_LONG_TERM_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('price_actions#priceActions/longTerm'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'price_actions/firstMinute',
    name: Routes.DISCOUNT_FIRST_MINUTE_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('price_actions#priceActions/firstMinute'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'price_actions/lastMinute',
    name: Routes.DISCOUNT_LAST_MINUTE_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('price_actions#priceActions/lastMinute'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'price_actions/actionPrice',
    name: Routes.DISCOUNT_SPECIAL_PRICE_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('price_actions#priceActions/actionPrice'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'discount_coupons/list',
    name: Routes.DISCOUNT_COUPONS_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('discount_coupons/list#priceActions/discountCoupons'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'settings/services',
    name: Routes.SERVICES_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('settings/services#services'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'stock',
    name: Routes.STOCK_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('stock'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'settings/stay_packages',
    name: Routes.STAY_PACKAGES_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('settings/stay_packages'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'settings/hotel_general_settings',
    name: Routes.HOTEL_GENERAL_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('settings/hotel_general_settings#hotelGeneralSettings/general'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'settings/hotel_general_settings_bank_accounts',
    name: Routes.HOTEL_GENERAL_BANK_ACCOUNTS,
    beforeEnter: guardRedirectToP1(
      'settings/hotel_general_settings#hotelGeneralSettings/bankAccounts',
    ),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'settings/facilities',
    name: Routes.HOTEL_AMENITIES_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('settings/facilities'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'settings/photogallery',
    name: Routes.HOTEL_PHOTOGALLERIES_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('settings/photogallery#photoGallery'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'settings/guest_categories',
    name: Routes.HOTEL_GUEST_CATEGORIES_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('settings/guest_categories'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'settings/objects',
    name: Routes.HOTEL_ROOMS_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('settings/objects'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'settings/connectors',
    name: Routes.HOTEL_CONNECTORS_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('settings/connectors#connectors/cardLocking'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'online_settings/payments',
    name: Routes.ONLINE_RESERVATIONS_PAYMENTS_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('online_settings/payments'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'online_settings/conditions',
    name: Routes.ONLINE_RESERVATIONS_TERMS_AND_CONDITIONS_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('online_settings/conditions'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'online_settings/privacy_policy',
    name: Routes.ONLINE_RESERVATIONS_PRIVACY_POLICY_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1(
      'online_settings/privacy_policy#onlineReservations/privacyPolicy',
    ),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'online_settings/reservation_systems',
    name: Routes.ONLINE_RESERVATIONS_SYSTEMS_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1(
      'online_settings/reservation_systems#onlineReservations/systems/partners',
    ),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'settings/web_page',
    name: Routes.WEB_GENERAL_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('settings/web_page#webPage/settings/general'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'settings/web_page/webSections',
    name: Routes.WEB_SECTIONS_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('settings/web_page#webSections'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'settings/web_page/news',
    name: Routes.WEB_NEWS_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('settings/web_page#news'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'settings/update_wp_data',
    name: Routes.WEB_UPDATE_WP_DATA,
    beforeEnter: guardRedirectToP1('settings/update_wp_data'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'settings/login_wp_admin',
    name: Routes.WEB_LOGIN_WP_ADMIN,
    beforeEnter: guardRedirectToP1('settings/login_wp_admin'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'settings/roles',
    name: Routes.ROLES_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('settings/roles#roles'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'settings/users',
    name: Routes.USERS_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('settings/users#users'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'settings/web_page_translation',
    name: Routes.DESCRIPTIONS_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('settings/web_page_translation#webPageTranslation'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'settings/market_codes',
    name: Routes.MARKET_CODES_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('settings/market_codes'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'settings/important_days',
    name: Routes.SIGNIFICANT_DATES_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('settings/important_days'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'system/settings/commonForm',
    name: Routes.SYSTEM_RESERVATION_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('system/settings#systemSettings/commonForm'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'system/settings/voucherForm',
    name: Routes.SYSTEM_VOUCHER_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('system/settings#systemSettings/voucherForm'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'system/settings/hotelAccountForm',
    name: Routes.SYSTEM_HOTEL_ACCOUNT_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('system/settings#systemSettings/hotelAccountForm'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'system/settings/reportsForm',
    name: Routes.SYSTEM_REPORTS_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('system/settings#systemSettings/reportsForm'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'system/settings/sender',
    name: Routes.SYSTEM_EMAIL_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('system/settings#systemSettings/sender'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'help/invoices',
    name: Routes.HELP_INVOICES_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('help/invoices#helpInvoices'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'help/tips',
    name: Routes.HELP_USER_TIPS_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('help/tips#helpTips'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'help/feedback',
    name: Routes.HELP_FEEDBACK_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('help/feedback'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'help/demo',
    name: Routes.HELP_DEMO_INDEX,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('help/demo'),
    component: UnreachableRouteComponentPlaceholder,
  },
  {
    path: 'settings/users#users|_editUser/:userId',
    name: Routes.USERS_DETAIL,
    meta: {
      isMobileFirst: false,
    },
    beforeEnter: guardRedirectToP1('settings/users#users|_editUser/:userId'),
    component: UnreachableRouteComponentPlaceholder,
  },
]
